// The wire call to the SH-1408 aggregation endpoint. Uses the app's shared
// `get` (auth handled by createCrud); the API base URL is prefixed explicitly,
// as every other service in `store/**/api.ts` does.

import { get } from '@user/store/services/crud'
import { ServiceConfig } from '@user/store/services/config'
import {
  MAX_QUERY_DAYS,
  MAX_TAKE,
  type CalendarEventDTO,
  type CalendarEventsResponse,
  type FetchCalendarParams,
} from './calendarContract'

const MAX_QUERY_MS = MAX_QUERY_DAYS * 24 * 60 * 60 * 1000

/** One page of one window — the raw endpoint call. */
export async function fetchCalendarEvents({
  start,
  end,
  skip = 0,
  take = 100,
  signal,
}: FetchCalendarParams): Promise<CalendarEventsResponse> {
  const query = new URLSearchParams({
    start: start.toISOString(),
    end: end.toISOString(),
    skip: String(skip),
    take: String(take),
  })

  const response = await get(
    `${ServiceConfig.baseAPIUrl}${ServiceConfig.calendar.events}?${query}`,
    {},
    { signal },
  )
  if (!response.ok) {
    throw new Error(`Calendar events request failed (${response.status})`)
  }
  return (await response.json()) as CalendarEventsResponse
}

/**
 * Split `[start, end)` into consecutive windows the server accepts. The cap is
 * measured in elapsed time, so a local-time month containing a DST fall-back is
 * 31 days + 1 hour and must be split rather than sent whole (it would 400).
 */
export function splitRange(
  start: Date,
  end: Date,
): { start: Date; end: Date }[] {
  const windows: { start: Date; end: Date }[] = []
  let cursor = start.getTime()
  const endMs = end.getTime()
  while (cursor < endMs) {
    const next = Math.min(cursor + MAX_QUERY_MS, endMs)
    windows.push({ start: new Date(cursor), end: new Date(next) })
    cursor = next
  }
  return windows
}

type EventsAndErrors = Pick<CalendarEventsResponse, 'events' | 'errors'>

async function fetchWindow(
  start: Date,
  end: Date,
  signal?: AbortSignal,
): Promise<EventsAndErrors> {
  const first = await fetchCalendarEvents({
    start,
    end,
    take: MAX_TAKE,
    signal,
  })
  const events = [...first.events]
  let errors = { ...first.errors }
  while (events.length < first.paging.total) {
    const page = await fetchCalendarEvents({
      start,
      end,
      skip: events.length,
      take: MAX_TAKE,
      signal,
    })
    if (page.events.length === 0) break
    events.push(...page.events)
    errors = { ...errors, ...page.errors }
  }
  return { events, errors }
}

// SH-2298: de-dup on sourceId, not id. Every SC-derived event (Activity / NAD /
// ECD) ships with id=0, so `${source}:${id}` collapses them all to "SC:0" and
// only the last survives. sourceId is required and globally unique per event
// (CRUD events use sourceId = id.toString(), so their key is unchanged).
const eventKey = (dto: CalendarEventDTO) => `${dto.source}:${dto.sourceId}`

/**
 * Every event overlapping `[start, end)`, however long the range or large the
 * result: windows are fetched in parallel, each followed to its `paging.total`.
 * An event spanning a window boundary comes back from both, so results are
 * de-duplicated.
 */
export async function fetchCalendarRange({
  start,
  end,
  signal,
}: Pick<
  FetchCalendarParams,
  'start' | 'end' | 'signal'
>): Promise<EventsAndErrors> {
  const results = await Promise.all(
    splitRange(start, end).map((w) => fetchWindow(w.start, w.end, signal)),
  )

  const byKey = new Map<string, CalendarEventDTO>()
  let errors: CalendarEventsResponse['errors'] = {}
  for (const result of results) {
    for (const dto of result.events) byKey.set(eventKey(dto), dto)
    errors = { ...errors, ...result.errors }
  }
  return { events: [...byKey.values()], errors }
}
